import { apiClient } from '@/apis/instances/api-client'
import { closeAlertPopup, showAlertPopup } from './sheet-alert'
import { queryClient } from '@/apis/instances/query-client'
import { v2ReservationListKeys } from '@/composables/queries/v2reservation/use-v2-reservation-list-query'
import { statsReservationDailyKeys } from '@/composables/queries/stats/use-stats-v2reservation-daily-find-query'
import { v2HReservationKeys } from '@/composables/queries/v2hreservation/use-v2-hreservation-find-query'
import { v2HReservationCountKeys } from '@/composables/queries/v2hreservation/use-v2-hreservation-count-query'
import { typeToString } from '@/util/type-to-string'
import { sheetFilters, type FilterItem } from './sheet-filters'
import { reservationSelection } from './reservation-selection'
import { sheetState } from './sheet-state'

/*
 * 예약 생성/수정/삭제 액션 — 구 ReservationController 대체.
 *
 * - 호출부(Right·ReservationMenu·DoctorColumnCell)가 axios 봉투(result.data.code/data)를
 *   읽는 계약을 유지하기 위해 apiClient 응답을 해체하지 않고 그대로 반환한다.
 * - "발송 확인 알럿을 띄우고 사용자의 선택을 기다렸다가 흐름을 잇는" 대기는
 *   호출별 클로저 resolve 로 처리한다 — 구 컨트롤러의 this.resolve 싱글턴(동시 호출 시
 *   유실 버그)을 제거.
 * - 저장 성공 시 예약 관련 캐시를 무효화한다 (재조회는 기존 refresh 흐름이 담당).
 */

type EnvelopeData = Record<string, unknown> & {
    _id?: string
    aiMessageType?: unknown
    statusID?: unknown
    isRemoved?: boolean
    createdAt?: string
    updatedAt?: string
}
type Envelope = { data: { code?: number | string; data?: EnvelopeData; message?: string } }
type ReservationModel = Record<string, unknown>
type StatusLike = { _id?: string; isCancelStatus?: boolean }
type HServiceLike = { closeReason?: unknown[]; lastLogin?: unknown; isUnregisteredUser?: boolean } | undefined
type OriginReservation = {
    startedAt?: unknown
    endedAt?: unknown
    groupID?: { _id?: string }
    columnID?: { _id?: string }
    programID?: { _id?: string }
    statusID?: StatusLike | string
} | undefined

const invalidateReservationCaches = (result: Envelope | undefined): void => {
    if (result?.data?.code != 0) { return }
    queryClient.invalidateQueries({ queryKey: v2ReservationListKeys.all, refetchType: 'none' })
    queryClient.invalidateQueries({ queryKey: statsReservationDailyKeys.all, refetchType: 'none' })
    queryClient.invalidateQueries({ queryKey: v2HReservationKeys.all, refetchType: 'none' })
    queryClient.invalidateQueries({ queryKey: v2HReservationCountKeys.all, refetchType: 'none' })
}

const normalizeId = (value: unknown): unknown =>
    typeof value === 'object' && value !== null ? (value as { _id?: string })._id : value

const isJoinedHService = (hservice: HServiceLike): boolean => {
    const closeReason = hservice?.closeReason ?? []
    return closeReason.length == 0 && hservice?.lastLogin != undefined
}

// MARK: - Network (봉투 유지)

async function requestCreateUnregistered(model: ReservationModel): Promise<Envelope> {
    sheetState.isPopupLoading = true
    try {
        const result = await apiClient.v2.apiV2ReservationElCreateUnregistered(model)
        invalidateReservationCaches(result)
        return result
    } finally {
        sheetState.isPopupLoading = false
    }
}

async function requestCreate(model: ReservationModel): Promise<Envelope> {
    sheetState.isPopupLoading = true
    model.userID = normalizeId(model.userID)
    model.hserviceID = normalizeId(model.hserviceID)
    model.groupID = normalizeId(model.groupID)
    model.columnID = normalizeId(model.columnID)
    model.statusID = normalizeId(model.statusID)
    try {
        const result = await apiClient.v3.reservationController(model as never)
        invalidateReservationCaches(result)
        return result
    } finally {
        sheetState.isPopupLoading = false
    }
}

async function requestCreateRegist(model: ReservationModel): Promise<Envelope> {
    sheetState.isPopupLoading = true
    try {
        const result = await apiClient.v2.apiV2ReservationElCreateRegist(model)
        invalidateReservationCaches(result)
        return result
    } finally {
        sheetState.isPopupLoading = false
    }
}

async function requestUpdate(model: ReservationModel): Promise<Envelope> {
    sheetState.isPopupLoading = true
    model.hserviceID = normalizeId(model.hserviceID)
    model.groupID = normalizeId(model.groupID)
    model.columnID = normalizeId(model.columnID)
    try {
        const result = await apiClient.v3.reservationControllerUpdate(model as never)
        invalidateReservationCaches(result)
        return result
    } finally {
        sheetState.isPopupLoading = false
    }
}

async function requestRemove(model: ReservationModel): Promise<Envelope> {
    sheetState.isPopupLoading = true
    try {
        const result = await apiClient.v3.reservationControllerRemove({ _id: (model as { _id?: string })._id } as never)
        invalidateReservationCaches(result)
        return result
    } finally {
        sheetState.isPopupLoading = false
    }
}

async function requestSendPush(newReservation: EnvelopeData): Promise<void> {
    sheetState.isPopupLoading = true
    let action = 'create'
    if (checkIsRestored(normalizeId(newReservation.statusID) as string)) {
        action = 'create'
    } else if (newReservation.isRemoved == true) {
        action = 'remove'
    } else if (checkIsStatusCancel(normalizeId(newReservation.statusID) as string)) {
        action = 'cancel'
    } else if (newReservation.createdAt != newReservation.updatedAt) {
        action = 'update'
    }
    const model = { action, reservationID: newReservation._id }
    try {
        if (newReservation.aiMessageType) {
            await apiClient.v3.reservationControllerSendAiPush(model as never)
        } else {
            await apiClient.v3.reservationControllerSendPush(model as never)
        }
    } finally {
        sheetState.isPopupLoading = false
    }
}

// MARK: - Reservation Check (구 컨트롤러와 동일 판단식 — 원본은 DataController.selectedReservation)

const originReservation = () => reservationSelection.selectedReservation as OriginReservation

export function checkIsModified(r: ReservationModel): boolean {
    const origin = originReservation()
    if (origin?.startedAt != r.startedAt) { return true }
    if (origin?.endedAt != r.endedAt) { return true }
    if (origin?.groupID?._id != r.groupID) { return true }
    if (origin?.columnID?._id != r.columnID) { return true }
    if (origin?.programID?._id != r.programID) { return true }
    const originStatusId = typeof origin?.statusID === 'object' ? origin?.statusID?._id : undefined
    if (originStatusId == r.statusID) { return false }
    if (checkIsStatusCancel(r.statusID as string)) { return true }
    if (checkIsRestored(r.statusID as string)) { return true }
    return false
}

function findStatus(statusID: string | undefined): StatusLike | undefined {
    return (sheetFilters.states as FilterItem[] as StatusLike[]).find((s) => s._id == statusID)
}

export function checkIsStatusCancel(statusID: string | undefined): boolean {
    return findStatus(statusID)?.isCancelStatus == true
}

export function checkIsRestored(statusID: string | undefined): boolean {
    const originStatus = originReservation()?.statusID
    const oStatus: StatusLike | undefined =
        typeToString(originStatus) == 'object' ? (originStatus as StatusLike) : findStatus(originStatus as string | undefined)
    if (oStatus?.isCancelStatus == true) {
        const status = findStatus(statusID)
        if (status && status.isCancelStatus == false) {
            return true
        }
    }
    return false
}

// MARK: - 발송 확인 알럿 (호출별 클로저 resolve — 싱글턴 제거)

function showAlertSendPushPopup(
    result: Envelope,
    isJoined: boolean,
    aiMessageType: unknown,
    resolve: (value: Envelope | undefined) => void,
): void {
    if (result.data.data) {
        result.data.data.aiMessageType = aiMessageType
    }
    const handleLeft = (r: Envelope) => {
        closeAlertPopup()
        resolve(r)
    }
    const handleRight = async (r: Envelope) => {
        await requestSendPush(r.data.data as EnvelopeData)
        resolve(r)
        closeAlertPopup()
    }
    showAlertPopup('AlertSendPush', handleLeft, handleRight, { isJoined, aiMessageType }, result, result)
}

// MARK: - Public actions (호출부 계약: axios 봉투 반환)

export async function createUnregisteredReservation(model: ReservationModel, usingSendMessageAlert = false): Promise<Envelope | undefined> {
    const result = await requestCreateUnregistered(model)
    if (usingSendMessageAlert) {
        // 구 컨트롤러도 result 없이 알럿을 띄웠고(사실상 미사용 분기) resolve 는 즉시 수행했다
        return new Promise((resolve) => {
            showAlertSendPushPopup(result, false, undefined, () => undefined)
            resolve(result)
        })
    }
    return result
}

export async function createReservation(model: ReservationModel, needForceResolve = false, usingSendMessageAlert = false): Promise<Envelope | undefined> {
    const result = await requestCreate(model)
    const newReservation = result?.data?.data
    if (newReservation == undefined) {
        return result
    }
    const isJoined = isJoinedHService(model?.hserviceID as HServiceLike)
    if (!usingSendMessageAlert) {
        return result
    }
    if (needForceResolve) {
        showAlertSendPushPopup(result, isJoined, undefined, () => undefined)
        return result
    }
    return new Promise((resolve) => {
        showAlertSendPushPopup(result, isJoined, undefined, resolve)
    })
}

export async function createRegistReservation(model: ReservationModel, usingSendMessageAlert = false): Promise<Envelope | undefined> {
    const result = await requestCreateRegist(model)
    const newReservation = result?.data?.data
    if (newReservation == undefined) {
        return result
    }
    if (!usingSendMessageAlert) {
        return result
    }
    const isJoined = isJoinedHService(model?.hserviceID as HServiceLike)
    return new Promise((resolve) => {
        showAlertSendPushPopup(result, isJoined, undefined, resolve)
    })
}

export async function updateReservation(
    model: ReservationModel,
    needForceResolve = false,
    usingSendMessageAlert = false,
    aiMessageType: unknown = undefined,
): Promise<Envelope | undefined> {
    const result = await requestUpdate(model)
    const newReservation = result?.data?.data
    if (newReservation == undefined) {
        return result
    }
    if (checkIsModified(newReservation) != true) {
        return result
    }
    const isJoined = isJoinedHService(model?.hserviceID as HServiceLike)
    if (!usingSendMessageAlert) {
        return result
    }
    if (needForceResolve) {
        showAlertSendPushPopup(result, isJoined, aiMessageType, () => undefined)
        return result
    }
    return new Promise((resolve) => {
        showAlertSendPushPopup(result, isJoined, aiMessageType, resolve)
    })
}

export async function removeReservation(
    model: ReservationModel,
    usingSendMessageAlert = false,
    aiMessageType: unknown = undefined,
): Promise<Envelope | undefined> {
    const hservice = model?.hserviceID as HServiceLike
    const isJoined = isJoinedHService(hservice)
    model.aiMessageType = aiMessageType

    return new Promise((resolve) => {
        const handleLeft = () => {
            closeAlertPopup()
            resolve(undefined)
        }
        const handleRight = async (confirmModel: ReservationModel) => {
            // 삭제 확정 — 미등록 고객이면 발송 알럿을 건너뛴다 (모델 변형 전 판단, 기존 동작)
            let useAlert = usingSendMessageAlert
            if ((confirmModel.hserviceID as HServiceLike)?.isUnregisteredUser) { useAlert = false }

            const result = await requestRemove(confirmModel)
            if (result.data.code != 0) {
                resolve(result)
                return
            }
            if (useAlert) {
                showAlertSendPushPopup(result, isJoined, aiMessageType, resolve)
            } else {
                closeAlertPopup()
                resolve(result)
            }
        }
        showAlertPopup('AlertRemoveConfirm', handleLeft, handleRight, undefined, undefined, model)
    })
}
